import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent,
  Button,
  Chip,
  Stack
} from '@mui/material';
import { LocationOn, Work, AccessTime } from '@mui/icons-material';
import api from '../../utils/axiosConfig';
import SimpleMVPSearch from './SimpleMVPSearch';

// Accessible MVP Search Results Component
// Focus: Simple, clean job results with accessibility compliance

const ResultsContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '2rem',
  
  [theme.breakpoints.down('md')]: {
    padding: '1rem'
  }
}));

const ResultsHeader = styled(Box)(({ theme }) => ({
  marginBottom: '2rem',
  textAlign: 'center',
  
  [theme.breakpoints.up('md')]: {
    textAlign: 'left'
  }
}));

const JobCard = styled(Card)(({ theme }) => ({
  marginBottom: '1rem',
  border: '1px solid',
  borderColor: theme.palette.mode === 'light' ? '#e0e0e0' : '#424242',
  backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#2d2d2d',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    borderColor: theme.palette.mode === 'light' ? '#cccccc' : '#666666',
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'light' 
      ? '0 4px 12px rgba(0,0,0,0.1)' 
      : '0 4px 12px rgba(0,0,0,0.3)'
  },
  
  '&:focus-within': {
    outline: `3px solid ${theme.palette.mode === 'light' ? '#4d9fff' : '#66b3ff'}`,
    outlineOffset: '2px'
  }
}));

const JobTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#333333' : '#ffffff',
  fontWeight: 600,
  marginBottom: '0.5rem',
  fontSize: '1.25rem',
  
  '&:hover': {
    color: '#009639'
  }
}));

const CompanyName = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#666666' : '#cccccc',
  fontWeight: 500,
  marginBottom: '0.5rem'
}));

const JobMeta = styled(Stack)(({ theme }) => ({
  direction: 'row',
  spacing: 2,
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '0.5rem',
  
  '& .MuiChip-root': {
    backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
    color: theme.palette.mode === 'light' ? '#333333' : '#ffffff',
    fontSize: '0.875rem'
  }
}));

const ViewJobButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#009639',
  color: '#ffffff',
  border: '2px solid #009639',
  minHeight: '40px',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 600,
  
  '&:hover': {
    backgroundColor: '#007a2d',
    borderColor: '#007a2d'
  },
  
  '&:focus': {
    outline: `3px solid ${theme.palette.mode === 'light' ? '#4d9fff' : '#66b3ff'}`,
    outlineOffset: '2px'
  }
}));

const NoResultsMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '3rem 1rem',
  color: theme.palette.mode === 'light' ? '#666666' : '#cccccc',
  
  '& h3': {
    color: theme.palette.mode === 'light' ? '#333333' : '#ffffff',
    marginBottom: '1rem'
  }
}));

const SimpleMVPResults = () => {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [abortController, setAbortController] = useState(null);
  
  const query = searchParams.get('q') || '';
  const location = searchParams.get('location') || '';

  // Fetch jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      if (!query) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError('');
      
      try {
        // Cancel previous request if exists
        if (abortController) {
          abortController.abort();
        }
        
        // Create new abort controller
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        
        const params = { q: query };
        if (location) params.location = location;
        
        const response = await api.get('/jobs', { 
          params,
          signal: newAbortController.signal 
        });
        
        if (response.data && response.data.jobs) {
          // Validate and sanitize job data
          const validJobs = response.data.jobs.filter(job => 
            job && 
            job.id && 
            job.title && 
            typeof job.title === 'string'
          );
          
          setJobs(validJobs);
          setTotalCount(response.data.pagination?.total || validJobs.length);
          
          // Track search analytics
          if (typeof window !== 'undefined' && window.plausible) {
            window.plausible('Job Search', {
              props: {
                query: query,
                location: location || 'all',
                results: validJobs.length,
                source: 'mvp_search'
              }
            });
          }
        } else {
          setJobs([]);
          setTotalCount(0);
        }
      } catch (err) {
        // Don't show error for cancelled requests
        if (err.name === 'AbortError') {
          return;
        }
        
        setError('Failed to load jobs. Please try again.');
        console.error('Job fetch error:', err);
      } finally {
        setLoading(false);
        setAbortController(null);
      }
    };
    
    fetchJobs();
    
    // Cleanup function
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [query, location]);

  // Handle new search
  const handleNewSearch = (searchData) => {
    const params = new URLSearchParams();
    params.set('q', searchData.q);
    if (searchData.location) params.set('location', searchData.location);
    
    // Use React Router for proper navigation
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Trigger search directly instead of page reload
    setJobs([]);
    setLoading(true);
    setError('');
    
    // Update URL params will trigger useEffect
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Safe company name extraction
  const getCompanyName = (job) => {
    if (job.company && typeof job.company === 'object') {
      return job.company.name || 'Company Name';
    }
    return job.companyName || job.company || 'Company Name';
  };

  // Safe job type extraction
  const getJobType = (job) => {
    if (job.type) {
      return job.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    }
    return job.jobType || '';
  };

  // Format job posting date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      return `${Math.floor(diffInDays / 30)} months ago`;
    } catch {
      return 'Recently';
    }
  };

  return (
    <ResultsContainer>
      {/* Search Interface */}
      <SimpleMVPSearch 
        onSearch={handleNewSearch}
        showTitle={false}
      />
      
      {/* Results Header */}
      <ResultsHeader>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress 
              size={50} 
              sx={{ color: '#009639' }}
              aria-label="Loading job results"
            />
          </Box>
        ) : (
          <>
            <Typography variant="h4" component="h1" gutterBottom>
              {query ? `Jobs for "${query}"` : 'All Jobs'}
              {location && ` in ${location}`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {totalCount > 0 
                ? `Found ${totalCount.toLocaleString()} job${totalCount === 1 ? '' : 's'}`
                : 'No jobs found'
              }
            </Typography>
          </>
        )}
      </ResultsHeader>
      
      {/* Error Message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ marginBottom: '2rem' }}
          role="alert"
        >
          {error}
        </Alert>
      )}
      
      {/* Job Results */}
      {!loading && !error && (
        <>
          {jobs.length > 0 ? (
            <Box role="main" aria-label="Job search results">
              {jobs.map((job) => (
                <JobCard key={job.id} component="article">
                  <CardContent>
                    <JobTitle variant="h5" component="h2">
                      {job.title}
                    </JobTitle>
                    
                    <CompanyName variant="h6" component="h3">
                      {getCompanyName(job)}
                    </CompanyName>
                    
                    <JobMeta direction="row" spacing={2}>
                      {job.location && (
                        <Chip 
                          icon={<LocationOn fontSize="small" />}
                          label={job.location}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      
                      {getJobType(job) && (
                        <Chip 
                          icon={<Work fontSize="small" />}
                          label={getJobType(job)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      
                      {(job.createdAt || job.postedAt) && (
                        <Chip
                          icon={<AccessTime fontSize="small" />}
                          label={formatDate(job.createdAt || job.postedAt)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </JobMeta>
                    
                    {job.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          marginBottom: '1rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {job.description}
                      </Typography>
                    )}
                    
                    <ViewJobButton
                      variant="contained"
                      href={`/jobs/${job.id}`}
                      aria-label={`View details for ${job.title} at ${getCompanyName(job)}`}
                    >
                      View Job Details
                    </ViewJobButton>
                  </CardContent>
                </JobCard>
              ))}
            </Box>
          ) : (
            <NoResultsMessage>
              <Typography variant="h3" component="h2" gutterBottom>
                No Jobs Found
              </Typography>
              <Typography variant="body1" gutterBottom>
                {query 
                  ? `We couldn't find any jobs matching "${query}"${location ? ` in ${location}` : ''}.`
                  : 'Try searching for a job title or keywords.'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try different keywords or check your spelling.
              </Typography>
            </NoResultsMessage>
          )}
        </>
      )}
    </ResultsContainer>
  );
};

export default SimpleMVPResults;